import React, { useState, useEffect } from 'react';
import { X, Link2, CheckCircle, Search } from 'lucide-react';
import { InvestigationType, InvestigationPriority, InvestigationTemplate, LinkableReport } from '../types';
import { investigationService } from '../investigationService';
import { getActiveSessionOrganizationCode } from '../../../../shared/api';
import { useEscapeKey } from '../../../../shared/useGlobalModalEscape';
import { useDialog } from '../../../../shared/DialogContext';

interface CreateInvestigationModalProps {
  onClose: () => void;
  onCreated: () => void;
}

export const CreateInvestigationModal: React.FC<CreateInvestigationModalProps> = ({
  onClose,
  onCreated,
}) => {
  useEscapeKey(onClose);
  const { showError, showWarning } = useDialog();

  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [type, setType] = useState<InvestigationType>('FORMAL');
  const [priority, setPriority] = useState<InvestigationPriority>('HIGH');
  const [confidentialityLevel, setConfidentialityLevel] = useState<'STANDARD' | 'CONFIDENTIAL' | 'STRICTLY_CONFIDENTIAL'>('STANDARD');
  const [occurrenceId, setOccurrenceId] = useState('');
  const [scope, setScope] = useState('');
  const [leadInvestigatorName, setLeadInvestigatorName] = useState('');
  const [loading, setLoading] = useState(false);

  // SafetyManager365 Parity Fields
  const [templates, setTemplates] = useState<InvestigationTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [linkedReport, setLinkedReport] = useState<LinkableReport | null>(null);
  const [flightNumber, setFlightNumber] = useState('');
  const [aircraftRegistration, setAircraftRegistration] = useState('');
  const [destinationAirport, setDestinationAirport] = useState('');
  const [oatCelsius, setOatCelsius] = useState<string>('');
  const [cloudHeightFeet, setCloudHeightFeet] = useState<string>('');
  const [findingsSummary, setFindingsSummary] = useState('');

  // Link to Report Search Modal
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkSearchTerm, setLinkSearchTerm] = useState('');
  const [linkCandidates, setLinkCandidates] = useState<LinkableReport[]>([]);
  const [loadingLinkables, setLoadingLinkables] = useState(false);

  // Load My Allocated Templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const tpls = await investigationService.getMyTemplates();
        if (tpls && tpls.length > 0) {
          setTemplates(tpls);
          setSelectedTemplateId(tpls[0].id);
        }
      } catch (err) {
        console.error('Failed to load investigation templates:', err);
      }
    };
    loadTemplates();
  }, []);

  const handleOpenLinkModal = async () => {
    setShowLinkModal(true);
    setLoadingLinkables(true);
    try {
      const candidates = await investigationService.getLinkableReports(linkSearchTerm);
      setLinkCandidates(candidates || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingLinkables(false);
    }
  };

  const handleSearchLinkables = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingLinkables(true);
    try {
      const candidates = await investigationService.getLinkableReports(linkSearchTerm);
      setLinkCandidates(candidates || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingLinkables(false);
    }
  };

  const handleSelectReport = (r: LinkableReport) => {
    setLinkedReport(r);
    setShowLinkModal(false);
    // Autofill fields from reporting module
    if (!title.trim()) setTitle(`Investigation: ${r.title}`);
    if (r.flight_number) setFlightNumber(r.flight_number);
    if (r.aircraft_registration) setAircraftRegistration(r.aircraft_registration);
    if (r.destination_airport) setDestinationAirport(r.destination_airport);
    if (r.oat_celsius !== undefined && r.oat_celsius !== null) setOatCelsius(String(r.oat_celsius));
    if (r.cloud_height_feet !== undefined && r.cloud_height_feet !== null) setCloudHeightFeet(String(r.cloud_height_feet));
    if (!summary.trim()) setSummary(r.description || r.title);
  };

  // Calculate default target completion date based on priority
  const getDefaultTargetDate = (p: InvestigationPriority): string => {
    const days = p === 'CRITICAL' ? 15 : p === 'HIGH' ? 30 : p === 'MEDIUM' ? 45 : 60;
    const target = new Date();
    target.setDate(target.getDate() + days);
    return target.toISOString().slice(0, 10);
  };

  const [targetCompletionDate, setTargetCompletionDate] = useState<string>(getDefaultTargetDate('HIGH'));

  const handlePriorityChange = (newP: InvestigationPriority) => {
    setPriority(newP);
    setTargetCompletionDate(getDefaultTargetDate(newP));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      showWarning('Please enter an investigation title.', 'Validation Error');
      return;
    }

    try {
      setLoading(true);
      await investigationService.createInvestigation({
        organizationId: getActiveSessionOrganizationCode() || undefined,
        title: title.trim(),
        summary: summary.trim() || undefined,
        type,
        priority,
        scope: scope.trim() || undefined,
        occurrenceId: occurrenceId.trim() || undefined,
        targetCompletionDate: targetCompletionDate ? new Date(targetCompletionDate).toISOString() : undefined,
        leadInvestigatorName: leadInvestigatorName.trim() || undefined,
        leadInvestigatorId: leadInvestigatorName.trim() ? `USR-${Date.now().toString().slice(-4)}` : undefined,
        status: leadInvestigatorName.trim() ? 'INVESTIGATOR_ASSIGNED' : 'INITIATED',
        createdBy: 'SAFETY_DIRECTOR',
        // SafetyManager365 Parity payload
        templateId: selectedTemplateId || undefined,
        linkedReportId: linkedReport?.id || undefined,
        flightNumber: flightNumber.trim() || undefined,
        aircraftRegistration: aircraftRegistration.trim() || undefined,
        destinationAirport: destinationAirport.trim() || undefined,
        oatCelsius: oatCelsius !== '' ? parseFloat(oatCelsius) : undefined,
        cloudHeightFeet: cloudHeightFeet !== '' ? parseInt(cloudHeightFeet, 10) : undefined,
        reportText: summary.trim() || title.trim(),
        findingsSummary: findingsSummary.trim() || undefined,
      });
      onCreated();
    } catch (err: any) {
      showError(err, 'Failed to Launch Investigation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-lg">
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Initiate Safety Investigation</h3>
            <p>SafetyManager365 & ICAO Annex 13 Flight Safety Investigation Dossier</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" title="Kapat" aria-label="Kapat">
            <X size={20} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
            {/* Template Selection & Link to Report Banner */}
            <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ fontWeight: 600, color: '#1e293b' }}>Investigation Template (Allocation Gated) *</label>
                <button
                  type="button"
                  onClick={handleOpenLinkModal}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    background: linkedReport ? '#dcfce7' : '#e0e7ff',
                    color: linkedReport ? '#15803d' : '#4338ca',
                    border: '1px solid',
                    borderColor: linkedReport ? '#86efac' : '#c7d2fe',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '12px',
                  }}
                >
                  <Link2 size={14} />
                  {linkedReport ? `Linked: ${linkedReport.report_number}` : 'Link to Report (Reporting Module)'}
                </button>
              </div>

              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.templateCode ? `[${t.templateCode}] ` : ''}{t.templateName || t.title}
                  </option>
                ))}
              </select>

              {linkedReport && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#15803d', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckCircle size={14} />
                  <span>Report data inherited: <strong>{linkedReport.report_number}</strong> — {linkedReport.title}</span>
                </div>
              )}
            </div>

            {/* Title */}
            <div className="form-field">
              <label>Investigation Title *</label>
              <input
                type="text"
                required
                placeholder="e.g. TC-JDM Dual Hydraulic Fluctuation on Final Approach"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            {/* Operational Flight & Weather Parameters */}
            <div style={{ background: '#f1f5f9', padding: '12px', borderRadius: '6px', marginBottom: '14px' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Operational & Meteorological Parameters (Inherited or Manual)
              </span>
              <div className="form-grid-2" style={{ marginTop: '8px' }}>
                <div className="form-field">
                  <label>Flight Number</label>
                  <input
                    type="text"
                    placeholder="e.g. TK1981"
                    value={flightNumber}
                    onChange={(e) => setFlightNumber(e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label>Aircraft Registration</label>
                  <input
                    type="text"
                    placeholder="e.g. TC-LND"
                    value={aircraftRegistration}
                    onChange={(e) => setAircraftRegistration(e.target.value)}
                  />
                </div>
              </div>
              <div className="form-grid-2">
                <div className="form-field">
                  <label>Destination Airport (ICAO)</label>
                  <input
                    type="text"
                    placeholder="e.g. LTFM"
                    value={destinationAirport}
                    onChange={(e) => setDestinationAirport(e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label>Outside Air Temp OAT (°C)</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="e.g. 14.5"
                    value={oatCelsius}
                    onChange={(e) => setOatCelsius(e.target.value)}
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Cloud Height (feet AGL)</label>
                <input
                  type="number"
                  placeholder="e.g. 2500"
                  value={cloudHeightFeet}
                  onChange={(e) => setCloudHeightFeet(e.target.value)}
                />
              </div>
            </div>

            {/* Type & Priority */}
            <div className="form-grid-2">
              <div className="form-field">
                <label>Investigation Type *</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as InvestigationType)}
                >
                  <option value="FORMAL">FORMAL (Standard Aircraft Incident)</option>
                  <option value="COMPREHENSIVE">COMPREHENSIVE (Major Event / HFACS)</option>
                  <option value="DESK">DESK (Rapid Review)</option>
                  <option value="SPECIAL">SPECIAL (Accountable Executive)</option>
                </select>
              </div>

              <div className="form-field">
                <label>Priority & SLA *</label>
                <select
                  value={priority}
                  onChange={(e) => handlePriorityChange(e.target.value as InvestigationPriority)}
                >
                  <option value="CRITICAL">CRITICAL (15 Days SLA)</option>
                  <option value="HIGH">HIGH (30 Days SLA)</option>
                  <option value="MEDIUM">MEDIUM (45 Days SLA)</option>
                  <option value="LOW">LOW (60 Days SLA)</option>
                </select>
              </div>
            </div>

            {/* Confidentiality & Target Completion */}
            <div className="form-grid-2">
              <div className="form-field">
                <label>Confidentiality Level *</label>
                <select
                  value={confidentialityLevel}
                  onChange={(e) => setConfidentialityLevel(e.target.value as any)}
                >
                  <option value="STANDARD">STANDARD (Safety Dept Visibility)</option>
                  <option value="CONFIDENTIAL">CONFIDENTIAL (Designated Team Only)</option>
                  <option value="STRICTLY_CONFIDENTIAL">STRICTLY CONFIDENTIAL (Lead & Dir Only)</option>
                </select>
              </div>

              <div className="form-field">
                <label>Target Completion Date (SLA)</label>
                <input
                  type="date"
                  value={targetCompletionDate}
                  onChange={(e) => setTargetCompletionDate(e.target.value)}
                />
              </div>
            </div>

            {/* Associated Occurrence ID & Lead Investigator */}
            <div className="form-grid-2">
              <div className="form-field">
                <label>Associated Occurrence ID / Ref (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. OCC-2026-00001"
                  value={occurrenceId}
                  onChange={(e) => setOccurrenceId(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Lead Investigator (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Capt. James Halpert (Chief Investigator)"
                  value={leadInvestigatorName}
                  onChange={(e) => setLeadInvestigatorName(e.target.value)}
                />
              </div>
            </div>

            {/* Scope */}
            <div className="form-field">
              <label>Terms of Reference & Investigation Scope</label>
              <input
                type="text"
                placeholder="e.g. Examine line maintenance procedure, hydraulic pack inspection log, and flight crew QRH compliance"
                value={scope}
                onChange={(e) => setScope(e.target.value)}
              />
            </div>

            {/* Summary / Report Text */}
            <div className="form-field">
              <label>Factual Occurrence Narrative (Report Text)</label>
              <textarea
                rows={3}
                placeholder="Factual narrative, flight data recorder retrieval status, or direct witnesses..."
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
              />
            </div>

            {/* Preliminary Findings Summary */}
            <div className="form-field">
              <label>Preliminary Findings / Investigator Analysis Summary</label>
              <textarea
                rows={2}
                placeholder="Optional preliminary findings or early technical observations..."
                value={findingsSummary}
                onChange={(e) => setFindingsSummary(e.target.value)}
              />
            </div>
          </div>

          {/* Footer Controls */}
          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Launching Dossier...' : 'Create & Initiate Investigation'}
            </button>
          </div>
        </form>
      </div>

      {/* Link to Report Selector Submodal */}
      {showLinkModal && (
        <div className="modal-backdrop" style={{ zIndex: 9999 }}>
          <div className="modal-card" style={{ maxWidth: '640px', width: '90%' }}>
            <div className="modal-header">
              <div className="modal-header-content">
                <h3>Link to Reporting Module</h3>
                <p>Select an approved Master or Independent Report to inherit parameters</p>
              </div>
              <button type="button" onClick={() => setShowLinkModal(false)} className="modal-close-btn" title="Kapat">
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSearchLinkables} style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                <input
                  type="text"
                  placeholder="Search by flight number, report number or title..."
                  value={linkSearchTerm}
                  onChange={(e) => setLinkSearchTerm(e.target.value)}
                  style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                />
                <button type="submit" className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Search size={16} /> Search
                </button>
              </form>

              {loadingLinkables ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>Loading candidate reports...</div>
              ) : linkCandidates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>No linkable reports found.</div>
              ) : (
                <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  {linkCandidates.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => handleSelectReport(c)}
                      style={{
                        padding: '10px 14px',
                        borderBottom: '1px solid #f1f5f9',
                        cursor: 'pointer',
                        borderRadius: '6px',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>{c.report_number}</span>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>{c.event_date?.slice(0, 10)}</span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#334155', marginBottom: '4px' }}>{c.title}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', display: 'flex', gap: '12px' }}>
                        {c.flight_number && <span>Flight: {c.flight_number}</span>}
                        {c.aircraft_registration && <span>A/C: {c.aircraft_registration}</span>}
                        {c.destination_airport && <span>Dest: {c.destination_airport}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" onClick={() => setShowLinkModal(false)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
